import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { useLocation, Link } from 'react-router-dom'
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Badge,
  List,
  Portal,
  Tooltip
} from '@mui/material'
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material'

// Reserve space at the viewport bottom for the dev environment banner
// (position: fixed, ~40px tall). Harmless when the banner is absent.
const VIEWPORT_MARGIN = 16
const BOTTOM_RESERVE = 48
const SHORT_VIEWPORT = '@media (max-height: 800px)'

interface SubMenuItem {
  path: string
  label: string
  icon?: React.ReactElement
  external?: boolean // Opens in new window/tab
  badge?: number    // Optional unread/attention count
}

interface SubMenuNavigationProps {
  label: string
  icon: React.ReactElement
  basePath: string
  subItems: SubMenuItem[]
  isActive: boolean
  isInCurrentSection?: boolean
  onActivate: () => void
  onDeactivate: () => void
  onStayActive: () => void
  badge?: number // Optional aggregate count displayed on the parent
  collapsed?: boolean
  railWidth?: number
}

const SubMenuNavigation: React.FC<SubMenuNavigationProps> = ({
  label,
  icon,
  basePath,
  subItems,
  isActive,
  isInCurrentSection = false,
  onActivate,
  onDeactivate,
  onStayActive,
  badge,
  collapsed = false,
  railWidth = 280
}) => {
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; height?: number; bridgeTop?: number } | null>(null)
  const menuItemRef = useRef<HTMLDivElement>(null)
  const subMenuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  const isPathActive = (path: string) => {
    // Only match exact paths to prevent multiple active states
    return location.pathname === path
  }

  // Parent should only be active when on exact base path, not when children are active
  const isParentActive = location.pathname === basePath

  // Initial sub-menu position - uses an estimated height; the layout effect
  // below refines it once we can measure the rendered sub-menu.
  const calculateMenuPosition = useCallback(() => {
    if (menuItemRef.current) {
      const rect = menuItemRef.current.getBoundingClientRect()
      if (rect.top >= 0 && rect.left >= 0) {
        const estimatedHeight = subItems.length * 48 + 16
        const maxTop = window.innerHeight - estimatedHeight - VIEWPORT_MARGIN - BOTTOM_RESERVE
        const clampedTop = Math.max(VIEWPORT_MARGIN, Math.min(rect.top, maxTop))
        setMenuPosition({
          top: clampedTop,
          left: railWidth + 8, // Current rail width + visual gap (8px)
          height: rect.height, // Bridge is anchored to the parent row, not the menu
          bridgeTop: rect.top
        })
      }
    }
  }, [subItems.length, railWidth])

  // After the sub-menu mounts, measure its real height and clamp `top` so
  // every link sits above the dev banner / viewport bottom. Handles cases
  // where the estimated height under-counted the rendered rows.
  useLayoutEffect(() => {
    if (!isActive || !menuPosition || !subMenuRef.current) return
    const actualHeight = subMenuRef.current.offsetHeight
    const maxTop = window.innerHeight - actualHeight - VIEWPORT_MARGIN - BOTTOM_RESERVE
    const clampedTop = Math.max(VIEWPORT_MARGIN, Math.min(menuPosition.top, maxTop))
    if (clampedTop !== menuPosition.top) {
      setMenuPosition(prev => (prev ? { ...prev, top: clampedTop } : prev))
    }
  }, [isActive, menuPosition])

  // Reflow on resize while the sub-menu is open
  useEffect(() => {
    if (!isActive) return
    const onResize = () => calculateMenuPosition()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [isActive, calculateMenuPosition])

  const handleMouseEnter = () => {
    calculateMenuPosition()
    onActivate() // Use shared state management
  }

  const handleMouseLeave = () => {
    onDeactivate() // Use shared state management
  }

  const handleSubMenuMouseEnter = () => {
    onStayActive() // Use shared state management
  }

  const handleSubMenuMouseLeave = () => {
    onDeactivate() // Use shared state management
  }

  return (
    <Box 
      ref={menuItemRef}
      sx={{ position: 'relative' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main menu item */}
      <ListItem disablePadding>
        {(() => {
          const button = (
            <ListItemButton
              component={Link}
              to={basePath}
              selected={isParentActive || isInCurrentSection}
              sx={{
                borderRadius: 2,
                mx: collapsed ? 0 : 1,
                mb: 0.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                textDecoration: 'none',
                color: 'inherit',
                '&.Mui-selected': {
                  backgroundColor: isParentActive
                    ? 'rgba(255, 255, 255, 0.15)'
                    : 'rgba(255, 255, 255, 0.08)', // Lighter for section active
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                [SHORT_VIEWPORT]: {
                  minHeight: 40,
                  py: 0.25,
                  mb: 0.25,
                },
              }}
            >
              <ListItemIcon sx={{
                color: 'white',
                minWidth: collapsed ? 0 : 40,
                justifyContent: 'center',
                [SHORT_VIEWPORT]: !collapsed ? { minWidth: 32 } : {},
              }}>
                {icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: 'white',
                      fontWeight: isParentActive ? 600 : (isInCurrentSection ? 500 : 400),
                    },
                    [SHORT_VIEWPORT]: {
                      '& .MuiListItemText-primary': { fontSize: '0.85rem' },
                    },
                  }}
                />
              )}
              {!!badge && badge > 0 && !collapsed && (
                <Badge
                  badgeContent={badge}
                  color="error"
                  sx={{
                    mr: 1.5,
                    '& .MuiBadge-badge': {
                      position: 'static',
                      transform: 'none',
                    },
                  }}
                />
              )}
              {!collapsed && (
                <ChevronRightIcon
                  sx={{
                    color: 'white',
                    fontSize: '1.2rem'
                  }}
                />
              )}
            </ListItemButton>
          )
          return collapsed ? (
            <Tooltip title={label} placement="right">
              <Box sx={{ width: '100%' }}>{button}</Box>
            </Tooltip>
          ) : button
        })()}
      </ListItem>

      {/* Slide-out sub-menu using Portal for proper positioning */}
      {isActive && menuPosition && (
        <Portal>
          {/* Invisible bridge to prevent hover loss - covers the gap */}
          <Box
            sx={{
              position: 'fixed',
              top: menuPosition.bridgeTop ?? menuPosition.top,
              left: railWidth - 5, // Start slightly before drawer edge for better coverage
              width: 25, // 5px before edge + 8px gap + 12px into sub-menu
              height: menuPosition.height || 48, // Use actual menu item height
              backgroundColor: 'transparent',
              zIndex: 1299, // Just below sub-menu
            }}
            onMouseEnter={handleSubMenuMouseEnter}
            onMouseLeave={handleSubMenuMouseLeave}
          />

          <Box
            ref={subMenuRef}
            sx={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              width: 240,
              maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2 + BOTTOM_RESERVE}px)`,
              overflowY: 'auto',
              backgroundColor: '#006b7d', // Slightly darker teal for sub-menu
              borderRadius: 2,
              boxShadow: '4px 0 12px rgba(0,0,0,0.2)',
              opacity: 1,
              transform: 'translateX(0)',
              transition: 'all 0.2s ease-in-out',
              zIndex: 1301, // Above MUI drawer (1200) and dev banner (1300)
            }}
            onMouseEnter={handleSubMenuMouseEnter}
            onMouseLeave={handleSubMenuMouseLeave}
          >
            <List sx={{ py: 1 }}>
              {subItems.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    component={item.external ? 'a' : Link}
                    {...(item.external
                      ? { href: item.path, target: '_blank', rel: 'noopener noreferrer' }
                      : { to: item.path }
                    )}
                    selected={!item.external && isPathActive(item.path)}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      textDecoration: 'none',
                      color: 'inherit',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon sx={{ color: 'white', minWidth: 36 }}>
                        {item.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: 'white',
                          fontWeight: !item.external && isPathActive(item.path) ? 600 : 400,
                          fontSize: '0.9rem'
                        }
                      }}
                    />
                    {!!item.badge && item.badge > 0 && (
                      <Badge
                        badgeContent={item.badge}
                        color="error"
                        sx={{
                          mr: 1,
                          '& .MuiBadge-badge': {
                            position: 'static',
                            transform: 'none',
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Portal>
      )}
    </Box>
  )
}

export default SubMenuNavigation